import { bindAwin } from '@/api/account';
import logger from '@/utils/client-logger';
import { env } from '@/utils/env';
import { getSetLocalStorage } from '@/utils/tools';
import { Provider, ProviderType, TrackingEvent } from '../types';

interface AwinConfig {
  merchantId: string;
  isEnabled?: boolean;
}

export class AwinProvider implements Provider {
  type = ProviderType.AWIN;
  isEnabled: boolean;
  loggingEnabled = false; // 公开属性，支持ReportingCenter访问
  private initialized = false;
  private config: AwinConfig;
  private scriptLoaded = false; // 标记脚本是否已加载

  constructor(config: AwinConfig) {
    this.config = config;
    this.isEnabled = config.isEnabled ?? true;
    this.loggingEnabled = env.NEXT_PUBLIC_ENV === 'local';
  }

  async init(): Promise<boolean> {
    try {
      if (typeof window === 'undefined') return false;
      if (this.initialized) return true;

      // 检查是否有配置 merchantId
      if (!this.config.merchantId) {
        if (this.loggingEnabled) {
          logger.warn('Awin merchantId is not configured');
        }
        return false;
      }

      // 动态加载 Awin 脚本
      await this.loadAwinScript();

      this.initialized = true;
      return true;
    } catch (error) {
      logger.error('Failed to initialize Awin:', error);
      return false;
    }
  }

  /**
   * 动态加载 Awin 脚本
   */
  private async loadAwinScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      // 避免重复加载
      if (this.scriptLoaded) {
        resolve();
        return;
      }

      // 检查脚本是否已经存在
      const existingScript = document.querySelector(
        `script[src*="dwin1.com/${this.config.merchantId}"]`,
      );
      if (existingScript) {
        this.scriptLoaded = true;
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = `https://www.dwin1.com/${this.config.merchantId}.js`;
      script.async = true;

      script.onload = () => {
        this.scriptLoaded = true;
        if (this.loggingEnabled) {
          logger.log('Awin script loaded successfully');
        }
        resolve();
      };

      script.onerror = (error) => {
        logger.error('Failed to load Awin script:', error);
        reject(error);
      };

      document.head.appendChild(script);
    });
  }

  track(event: TrackingEvent): void {
    // Awin 的事件追踪都是靠后端的
    if (this.loggingEnabled) {
      logger.log('Awin tracking event:', event);
    }
  }

  async bindAwin(token?: string, onSuccess?: () => void): Promise<void> {
    // 绑定awin
    // 从 URL 参数或 localStorage 获取 awc
    const urlParams = new URLSearchParams(window.location.search);
    const awc = urlParams.get('awc') || getSetLocalStorage.get('awc');

    if (awc) {
      const date = new Date().getTime();
      // 设置 30 天过期时间
      getSetLocalStorage.set('awc', awc, date + 30 * 24 * 60 * 60 * 1000);

      if (token) {
        try {
          // 【1008860】前端判断传参为空，不调接口
          const bindRes = awc ? await bindAwin({ awc }) : {};

          // 绑定成功
          if (bindRes?.code === 200) {
            if (onSuccess) {
              onSuccess();
            }
            // 绑定成功删除缓存中的awc，awin平台设置的有链接30天有效期
            localStorage.removeItem('awc');
          }
        } catch (e) {
          logger.error('bindAwin error', e);
        }
      }
    }
  }

  /**
   * 启用Awin日志
   */
  enableLog(): void {
    this.loggingEnabled = true;
  }

  /**
   * 禁用Awin日志
   */
  disableLog(): void {
    this.loggingEnabled = false;
  }

  isInitialized(): boolean {
    return this.initialized;
  }
}
